import { useLocalStorageValue } from "@react-hookz/web";
import { useAtomValue } from "jotai";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { NavLink, useLocation } from "react-router-dom";

import { Send } from "~/components/icons/send";
import { ShopifyModal } from "~/components/shopify-plugin/shopify-modal";
import { Button } from "~/components/ui/button";
import { cn } from "~/components/ui/cn";
import { AppIcon } from "~/components/ui/icons/app";
import { ExternalIcon } from "~/components/ui/icons/external";
import { SettingsIcon } from "~/components/ui/icons/settings";
import { ShopifyPluggedIcon } from "~/components/ui/icons/shopify-plugged";
import { Sidebar, SidebarContent } from "~/components/ui/sidebar";
import { track } from "~/libs/analytics";
import { brandState } from "~/stores/brand";
import { activeSaleState } from "~/stores/sales";

import { SetupContactCopyButton } from "./setup-contact";

export const SaleConfiguration: React.FC = () => {
  const { t } = useTranslation();
  const brand = useAtomValue(brandState);
  const { search } = useLocation();
  const activeSale = useAtomValue(activeSaleState);
  const [isOpen, setIsOpen] = useState(false);

  const { set: setPreviewRequiredShown } = useLocalStorageValue(
    `preview-sale-${activeSale?.id ?? ""}`,
    {
      initializeWithValue: false,
    },
  );

  if (!brand) {
    return null;
  }

  const hasActionToBeTaken = brand.has_shopify && !brand.shopify?.shop;

  return (
    <section className="flex flex-col gap-6 rounded-lg border border-gray-100 bg-white px-[1.4375rem] py-6">
      <div className="flex items-center gap-3">
        <div
          className={cn("rounded border p-1", {
            "border-[#F3D9B3] bg-[#FBF2E6] text-orange-600": hasActionToBeTaken,
            "border-gray-100 bg-[#FBFBFB] text-gray-900": !hasActionToBeTaken,
          })}
        >
          <SettingsIcon className="size-6" />
        </div>
        <span className="text-sm font-bold text-gray-900">
          {t("home.sale_configuration.sale_configuration")}
        </span>
      </div>
      <div className="divide-y divide-gray-100 rounded-md border border-gray-100">
        {activeSale?.setup_ready ? (
          <div className="flex items-center gap-4 p-6">
            <AppIcon className="size-8 shrink-0 text-gray-700" />
            <div className="flex flex-1 flex-col gap-1">
              <span className="text-sm font-semibold text-gray-900">
                {t("home.sale_configuration.setup_ready")}
              </span>
              <span className="text-xs font-normal text-gray-900">
                {t("home.sale_configuration.setup_ready_subtitle")}
              </span>
              {activeSale?.setup_email ? (
                <SetupContactCopyButton
                  email={activeSale.setup_email}
                  from="home"
                />
              ) : null}
            </div>
            <button
              onClick={() => {
                track("ViewSetup", {
                  from: "Home",
                });
                window.open(activeSale?.preview_link, "_blank");
                setPreviewRequiredShown("true");
              }}
              className="flex items-center rounded border border-gray-100 bg-[#FBFBFB] px-4 py-2 text-sm font-semibold text-gray-900 hover:bg-gray-50 focus-visible:ring-2 focus-visible:ring-gray-100 disabled:bg-gray-100 disabled:text-[#9DA0A1]"
            >
              <ExternalIcon className="mr-1 size-4" />
              {t("home.sale_configuration.view_setup")}
            </button>
          </div>
        ) : null}

        {brand.has_shopify ? (
          <div className="flex items-center gap-4 p-6">
            <ShopifyPluggedIcon title="Shopify" className="size-8 shrink-0" />
            <span className="flex-1 text-sm font-semibold text-gray-900">
              {t("home.sale_configuration.integrate_shopify")}
            </span>
            {brand.shopify?.shop ? (
              <button
                type="button"
                onClick={() => {
                  setIsOpen(true);
                  track("OpenModalShopifyIntegration", {
                    from: "Settings",
                    mode: "Edit",
                  });
                }}
                className="flex items-center rounded border border-gray-100 bg-[#FBFBFB] px-4 py-2 text-sm font-semibold text-gray-900 hover:bg-gray-50 focus-visible:ring-2 focus-visible:ring-gray-100 disabled:bg-gray-100 disabled:text-[#9DA0A1]"
              >
                {t("home.sale_configuration.view_settings")}
              </button>
            ) : (
              <Button
                type="button"
                onClick={() => {
                  setIsOpen(true);
                  track("OpenModalShopifyIntegration", {
                    from: "Home",
                    mode: "Edit",
                  });
                }}
              >
                {t("home.sale_configuration.integrate")}
              </Button>
            )}
          </div>
        ) : null}

        <div className="flex items-center gap-4 p-6">
          <Send className="size-8 shrink-0 text-gray-700" />
          <span className="flex-1 text-sm font-semibold text-gray-900">
            {t("home.sale_configuration.invite_other_members")}
          </span>
          <NavLink
            to={`/${brand.id}/settings/member${search}`}
            className="flex items-center rounded border border-gray-100 bg-[#FBFBFB] px-4 py-2 text-sm font-semibold text-gray-900 hover:bg-gray-50 focus-visible:ring-2 focus-visible:ring-gray-100 disabled:bg-gray-100 disabled:text-[#9DA0A1]"
          >
            {t("home.sale_configuration.view_settings")}
          </NavLink>
        </div>
      </div>
      <Sidebar
        open={isOpen}
        onOpenChange={(isOpen) => {
          setIsOpen(isOpen);
        }}
      >
        <SidebarContent className="mt-18 sm:!w-[37.5rem] lg:mt-0">
          <ShopifyModal setIsOpen={setIsOpen} />
        </SidebarContent>
      </Sidebar>
    </section>
  );
};
